import React, { ReactNode, useEffect, useState } from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  AutocompletePaperSlotPropsOverrides,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteValue,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";

import { keepPreviousData } from "@tanstack/react-query";
import _ from "lodash";

import { DefaultTextField } from "@components/Forms/DefaultInputs";
import { useSelectedResources } from "@features/filters";
import {
  resourceImplementsPagination,
  routeResourceTypes,
  useApiResource,
} from "@services/api";
import { cn, html } from "@utils/tailwind";
import useDebouncedValue from "@utils/useDebouncedValue";

const DEFAULT_PAGE_SIZE = 20;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Loading = html.div`h-10 rounded-md bg-neutral-100 animate-pulse`;

const getIdArrayFromUnkownValue = (value): any[] => {
  if (Array.isArray(value)) {
    return value.map((v) => v?.id ?? v);
  }
  return value ? [value?.id ?? value] : [];
};

export type ResourceAutocompleteProps<
  TResourceName extends keyof routeResourceTypes,
  T,
  Multiple extends boolean,
  DisableClearble extends boolean,
  FreeSolo extends boolean,
> = Omit<
  Partial<AutocompleteProps<T, Multiple, DisableClearble, FreeSolo>>,
  "value"
> & {
  resource: TResourceName;
  resourceFilterName?: string | false;
  label?: string;
  error?: boolean;
  helperText?: ReactNode;
  getOptionText?: (option: T) => string;
  getOptionSecondaryText?: string | ((option: T) => string);
  requestParams?: Record<string, any>;
  fetchOnMount?: boolean;
  clearSearchOnSelect?: boolean;
  renderInput?: (
    params: AutocompleteRenderInputParams & {
      isLoading?: boolean;
      error?: boolean;
      helperText?: ReactNode;
    }
  ) => React.JSX.Element;
  /* Tap options lets us modify the resource data before it's given as options */
  tapOptions?: (options: T[]) => T[];
  minimumCharacters?: number;
  /** Accepts a resource object, string ID, or array of either (multi-select) */
  value?: AutocompleteValue<T | string, Multiple, DisableClearble, FreeSolo>;
};

declare module "@mui/material/Autocomplete" {
  interface AutocompletePaperSlotPropsOverrides {
    isFetching?: boolean;
    isTruncated?: boolean;
  }
}

type ResourceAutocompletePaperProps = React.ComponentProps<typeof Paper> &
  AutocompletePaperSlotPropsOverrides;

/** Module-level so its identity is stable: an inline component would remount the options on every render. */
const ResourceAutocompletePaper = ({
  children,
  className,
  isFetching,
  isTruncated,
  ...props
}: ResourceAutocompletePaperProps) => (
  <Paper
    {...props}
    className={cn("relative border border-neutral-300 min-w-max", className)}
  >
    {children}
    {isTruncated && !isFetching && (
      <div className="text-sm text-primary-600 px-3 py-1">
        Showing top {DEFAULT_PAGE_SIZE} results
      </div>
    )}
    {isFetching && (
      <LinearProgress className="absolute bottom-0 left-0 right-0" />
    )}
  </Paper>
);

export const ResourceAutocomplete = <
  TResourceName extends keyof routeResourceTypes,
  Multiple extends boolean = false,
  DisableClearble extends boolean = false,
>({
  label,
  resource,
  resourceFilterName = "name",
  requestParams,
  value,
  onChange,
  fetchOnMount = false,
  getOptionText,
  getOptionSecondaryText,
  tapOptions = (opts) => opts,
  minimumCharacters = 0,
  error,
  helperText,
  renderInput,
  ...props
}: ResourceAutocompleteProps<
  TResourceName,
  routeResourceTypes[TResourceName],
  Multiple,
  DisableClearble,
  false
>) => {
  const pageSize = requestParams?.page?.size ?? DEFAULT_PAGE_SIZE;
  const [searchString, setSearchString] = useState<string>("");
  const [fetchData, setFetchData] = useState(fetchOnMount);

  const [areAllResourcesOnFirstPage, setAreAllResourcesOnFirstPage] = useState(
    !resourceImplementsPagination(resource)
  );

  const { multiple } = props;

  // no need to query the api if all resources are on the first page
  const shouldFilter = !!resourceFilterName && !areAllResourcesOnFirstPage;

  const debouncedSearchString = useDebouncedValue(searchString, 300);
  const enabled =
    fetchData && debouncedSearchString.length >= minimumCharacters;

  const { data, isFetching } = useApiResource(resource, {
    params: {
      ...requestParams,
      filter: {
        ...(shouldFilter && { [resourceFilterName]: debouncedSearchString }),
        ...requestParams?.filter,
      },
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });

  const valueIds = getIdArrayFromUnkownValue(value);
  const { data: selectedValues } = useSelectedResources(
    resource,
    valueIds,
    areAllResourcesOnFirstPage
  );

  let options: routeResourceTypes[TResourceName][] = _.uniqBy(
    [...selectedValues, ...(data ?? [])],
    "id"
  );
  options = tapOptions(options);
  options = getOptionText
    ? options.map((opt) => ({ ...opt, name: getOptionText(opt) }))
    : options;

  const getOpObj = (option) =>
    option?.id ? option : options.find((o) => o.id === option);

  useEffect(() => {
    if (
      enabled &&
      !isFetching &&
      !areAllResourcesOnFirstPage &&
      !debouncedSearchString &&
      data &&
      data.length !== pageSize
    ) {
      setAreAllResourcesOnFirstPage(true);
    }
  }, [
    data,
    isFetching,
    debouncedSearchString,
    areAllResourcesOnFirstPage,
    pageSize,
    enabled,
  ]);

  if (valueIds.length > 0 && !getOpObj(valueIds[0])) {
    return <Loading />;
  }

  return (
    <Autocomplete
      size="small"
      value={
        (value || (props.multiple ? [] : null)) as AutocompleteValue<
          routeResourceTypes[TResourceName],
          Multiple,
          DisableClearble,
          false
        >
      }
      options={options}
      inputValue={searchString}
      onInputChange={(_, value, reason) => {
        if (reason !== "reset" || !props.disableCloseOnSelect) {
          setSearchString(value); // don't reset search string if popper is staying open
        }
      }}
      onChange={(event, newValue, reason) => {
        if (props.clearSearchOnSelect) {
          setSearchString("");
        }
        onChange?.(event, newValue, reason);
      }}
      onOpen={() => setFetchData(true)}
      onClose={() => setFetchData(false)}
      isOptionEqualToValue={(option, value) =>
        option.id === getOpObj(value)?.id
      }
      noOptionsText={
        isFetching ? (
          <CircularProgress size={20} className="-mt-2" />
        ) : enabled ? (
          "No options"
        ) : (
          "Enter text to start searching"
        )
      }
      filterOptions={(options, { inputValue }) => {
        if (!inputValue) return options;
        return options.filter(
          (option) =>
            getOpObj(option)
              ?.name.toLowerCase()
              .includes(inputValue.toLowerCase()) ||
            (getOptionSecondaryText &&
              (typeof getOptionSecondaryText === "string"
                ? _.get(getOpObj(option), getOptionSecondaryText)
                : getOptionSecondaryText(getOpObj(option))
              )
                .toLowerCase()
                .includes(inputValue.toLowerCase()))
        );
      }}
      getOptionLabel={(option) => getOpObj(option)?.name ?? ""}
      renderOption={({ key: _key, ...props }, option, { selected }) => (
        <li {...props} key={option.id} className={cn(props.className, "group")}>
          {multiple && (
            <Checkbox
              key="checkbox"
              edge="start"
              icon={icon}
              checkedIcon={checkedIcon}
              checked={selected}
              className="-my-2"
            />
          )}
          <Typography noWrap key="label">
            {"name" in option ? option.name : option.id}
          </Typography>
          {getOptionSecondaryText && (
            <Typography
              key="secondary-text"
              className="text-neutral-400 text-sm ml-auto group-hover:text-neutral-700"
            >
              {typeof getOptionSecondaryText === "string"
                ? _.get(option, getOptionSecondaryText)
                : getOptionSecondaryText(option)}
            </Typography>
          )}
        </li>
      )}
      slots={{ paper: ResourceAutocompletePaper }}
      slotProps={{
        paper: {
          isFetching,
          isTruncated: !!data && data.length === pageSize,
        },
      }}
      renderInput={
        renderInput
          ? (params) =>
              renderInput({
                ...params,
                isLoading: isFetching,
                error,
                helperText,
              })
          : (params) => (
              <DefaultTextField
                {...params}
                label={label}
                error={error}
                helperText={helperText}
                InputProps={{
                  ...params.InputProps,
                  // https://stackoverflow.com/a/30976223
                  autoComplete: "one-time-code",
                  endAdornment: <>{params.InputProps.endAdornment}</>,
                }}
              />
            )
      }
      {...props}
    />
  );
};
